<template>
  <div>
    <AppHeader />
    <div class="theme-colors-container">
      <!-- Background Color Picker -->
      <div class="color-picker-box">
        <label for="bg-color-picker">Select Background Color:</label>
        <input 
          type="color" 
          id="bg-color-picker" 
          v-model="bgSelectedColor" 
          @input="updateColorPreview"
        >
        <div class="color-preview" :style="{ backgroundColor: bgSelectedColor }"></div>
      </div>

      <!-- Font Color Picker -->
      <div class="color-picker-box">
        <label for="font-color-picker">Select Font Color:</label>
        <input 
          type="color" 
          id="font-color-picker" 
          v-model="fontSelectedColor" 
          @input="updateColorPreview"
        >
        <div class="color-preview" :style="{ backgroundColor: fontSelectedColor }"></div>
      </div>

      <button @click="updateThemeColors" :disabled="isUpdating">
        {{ isUpdating ? 'Updating...' : 'Update Colors' }}
      </button>
      <p v-if="message" :class="['message', messageType]">{{ message }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ThemeColorPickerComponent',
  data() {
    return {
      bgSelectedColor: '#FFFFFF',
      fontSelectedColor: '#000000',
      isUpdating: false,
      message: '',
      messageType: '', 
      appId: null,
      isLoading: false
    };
  },
  mounted() {
    this.getAppIdFromStorage();
    this.fetchThemeColors();
  },
  methods: {
    getAppIdFromStorage() {
      this.appId = localStorage.getItem('appId');
      if (!this.appId) {
        this.showMessage('App ID not found in localStorage', 'error');
      }
    },
    
    async fetchThemeColors() {
      if (!this.appId) return;
      
      this.isLoading = true;
      this.$root.isLoading = true;
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${this.$BASE_API_URL}/api/apps/${this.appId}/theme-colors`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.data) {
          this.bgSelectedColor = data.data.background_color || '#FFFFFF';
          this.fontSelectedColor = data.data.font_color || '#000000';
          
          // Update CSS variables
          document.documentElement.style.setProperty('--bg-color', this.bgSelectedColor);
          document.documentElement.style.setProperty('--font-color', this.fontSelectedColor);
          
          // Update global settings
          this.$globalAppSettings.backgroundColor = this.bgSelectedColor;
          this.$globalAppSettings.fontColor = this.fontSelectedColor;
          window.__globalAppSettings = {
            ...window.__globalAppSettings,
            backgroundColor: this.bgSelectedColor,
            fontColor: this.fontSelectedColor
          };
        }
      } catch (error) {
        console.error('Error fetching theme colors:', error);
        this.showMessage('Failed to load theme colors', 'error');
      } finally {
        this.isLoading = false;
        this.$root.isLoading = false;
      }
    },

    updateColorPreview() {
      // Update CSS variables in real-time as user picks colors
      document.documentElement.style.setProperty('--bg-color', this.bgSelectedColor);
      document.documentElement.style.setProperty('--font-color', this.fontSelectedColor);
    },

    async updateThemeColors() {
      if (!this.appId) {
        this.showMessage('App ID is missing', 'error');
        return;
      }

      this.isUpdating = true;
      this.message = '';
      const token = localStorage.getItem('authToken');

      try {
        const response = await fetch(`${this.$BASE_API_URL}/api/apps/${this.appId}/theme-colors`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            background_color: this.bgSelectedColor,
            font_color: this.fontSelectedColor
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const successMessage = data.message || 'Theme colors updated successfully!';
        this.showMessage(successMessage, 'success');
        
        // Update CSS variables
        document.documentElement.style.setProperty('--bg-color', this.bgSelectedColor);
        document.documentElement.style.setProperty('--font-color', this.fontSelectedColor);
        
        // Update global settings and storage
        this.$globalAppSettings.backgroundColor = this.bgSelectedColor;
        this.$globalAppSettings.fontColor = this.fontSelectedColor;
        window.__globalAppSettings = {
          ...window.__globalAppSettings,
          backgroundColor: this.bgSelectedColor,
          fontColor: this.fontSelectedColor
        };
        localStorage.setItem('appBackgroundColor', this.bgSelectedColor);
        localStorage.setItem('appFontColor', this.fontSelectedColor);
        
      } catch (error) {
        console.error('Error updating theme colors:', error);
        let errorMsg = 'Failed to update theme colors';
        
        if (error.response) {
          errorMsg = error.response.data?.message || 
                    error.response.data?.error || 
                    error.response.statusText;
        } else if (error.message) {
          errorMsg = error.message;
        }
        
        this.showMessage(errorMsg, 'error');
      } finally {
        this.isUpdating = false;
      }
    },

    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type;
      setTimeout(() => {
        this.message = '';
        this.messageType = '';
      }, 5000);
    }
  }
};
</script>

<style scoped>
.theme-colors-container {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 1rem;
  max-width: 800px;
  margin: 0 auto;
}

.color-picker-box {
  border: 2px solid #ffff;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 250px;
}

label {
  display: block;
  margin-bottom: 15px;
  font-weight: bold;
  font-size: 1.1rem;
}

input[type="color"] {
  width: 80px;
  height: 80px;
  margin: 0 auto 15px;
  display: block;
  border: 2px solid;
  border-radius: 8px;
  cursor: pointer;
}

.color-preview {
  display: block;
  width: 100px;
  height: 100px;
  border: 2px solid #ccc;
  border-radius: 8px;
  margin: 0 auto 20px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

button {
  padding: 10px 25px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;
  margin-top: 10px;
}

button:hover {
  background-color: #45a049;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.message {
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
}

.message.success {
  background-color: #dff0d8;
  color: #3c763d;
}

.message.error {
  background-color: #f2dede;
  color: #a94442;
}
</style>